import { MoreHorizontal, Plus, ServerCog } from 'lucide-react';
import { useState } from 'react';

import { PageHeader } from '@/components/common/PageHeader';
import { QueryErrorView } from '@/components/common/QueryErrorView';
import { Can } from '@/components/permission/Can';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { useToast } from '@/components/shared/Toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { formatDate } from '@/features/common/format';
import { mutationErrorMessage } from '@/features/common/apiError';
import { useCan } from '@/features/permissions/useCan';

import { ApiKeyOnceModal, type KeyModalState } from './ApiKeyOnceModal';
import { NewEnvironmentWizard } from './NewEnvironmentWizard';
import { useListEnvironmentsQuery, useRotateEnvironmentKeyMutation } from './reportsApi';
import { REPORTS_PERMISSIONS } from './types';
import type { EnvironmentView } from './types';

const KEY_PREFIX_TOOLTIP =
  'Only the prefix is stored — the full key was shown once when the environment was created or rotated.';
const ROTATE_DENIED_TOOLTIP =
  "You don't have permission to rotate keys. Ask an organization owner or admin.";

/**
 * Environments panel (design §8.1) — the tab under
 * `…/reports/:reportProjectId/environments`. Mirrored DataTable (5 columns,
 * pageSize 10); the row menu carries Rotate. The plaintext key never lands
 * here: rows show the stored prefix only (P6), and create/rotate responses are
 * handed straight to `ApiKeyOnceModal` as component state.
 */
export function EnvironmentsPanel({ reportProjectId }: { reportProjectId: string }) {
  const toast = useToast();
  const listQuery = useListEnvironmentsQuery(reportProjectId);
  const canRotate = useCan(
    REPORTS_PERMISSIONS.environmentRotateKey.action,
    REPORTS_PERMISSIONS.environmentRotateKey.resource,
  );

  const [wizardOpen, setWizardOpen] = useState(false);
  const [keyModal, setKeyModal] = useState<KeyModalState | null>(null);
  const [rotateTarget, setRotateTarget] = useState<EnvironmentView | null>(null);
  const [rotate, rotateState] = useRotateEnvironmentKeyMutation();

  const addButton = (
    <Button size="sm" onClick={() => setWizardOpen(true)}>
      <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
      New environment
    </Button>
  );

  async function handleRotateConfirm() {
    if (!rotateTarget) {
      return;
    }
    try {
      const result = await rotate({
        environmentId: rotateTarget.id,
        reportProjectId,
      }).unwrap();
      toast.success('Key rotated.');
      setRotateTarget(null);
      if (result?.apiKey) {
        setKeyModal({
          variant: 'rotate',
          environmentName: result.environment?.name ?? rotateTarget.name,
          environmentId: result.environment?.id ?? rotateTarget.id,
          apiKey: result.apiKey,
        });
      }
    } catch (error) {
      toast.error(mutationErrorMessage(error, "Couldn't rotate key."));
    }
  }

  const columns: DataTableColumn<EnvironmentView>[] = [
    {
      id: 'environment',
      header: 'Environment',
      cell: (env) => (
        <div className="min-w-0">
          <div className="text-sm font-medium">{env.name}</div>
          <div className="text-xs text-muted-foreground">{environmentMetaLine(env)}</div>
        </div>
      ),
    },
    {
      id: 'apiKey',
      header: 'API key',
      cell: (env) =>
        env.apiKeyPrefix ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="cursor-help font-mono text-xs">{env.apiKeyPrefix}…</span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">{KEY_PREFIX_TOOLTIP}</TooltipContent>
          </Tooltip>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (env) => (
        <Badge variant={env.status === 'paused' ? 'outline' : 'secondary'}>
          {env.status === 'paused' ? 'Paused' : 'Active'}
        </Badge>
      ),
    },
    {
      id: 'created',
      header: 'Created',
      cell: (env) => (
        <span className="text-sm text-muted-foreground">{formatDate(env.createdAt)}</span>
      ),
    },
    {
      id: 'actions',
      header: '',
      srHeader: 'Actions',
      className: 'text-right',
      headerClassName: 'text-right',
      cell: (env) => {
        const disabled = canRotate.allowed !== true;
        const item = (
          <DropdownMenuItem disabled={disabled} onSelect={() => setRotateTarget(env)}>
            Rotate API key…
          </DropdownMenuItem>
        );
        const menu = (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Row actions">
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              {canRotate.allowed === false ? (
                <Tooltip>
                  <TooltipTrigger asChild>{item}</TooltipTrigger>
                  <TooltipContent className="max-w-xs">{ROTATE_DENIED_TOOLTIP}</TooltipContent>
                </Tooltip>
              ) : (
                item
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
        return <div className="flex justify-end">{menu}</div>;
      },
    },
  ];

  return (
    <section aria-labelledby="environments-title">
      <PageHeader
        title="Environments"
        titleAs="h2"
        titleId="environments-title"
        className="mb-4"
        actions={
          <Can
            action={REPORTS_PERMISSIONS.environmentCreate.action}
            resource={REPORTS_PERMISSIONS.environmentCreate.resource}
          >
            {addButton}
          </Can>
        }
      />

      {listQuery.isLoading ? (
        <LoadingRows label="Loading environments…" columns={5} />
      ) : listQuery.isError ? (
        <QueryErrorView
          error={listQuery.error}
          label="environments"
          onRetry={() => void listQuery.refetch()}
        />
      ) : (
        <DataTable
          columns={columns}
          data={listQuery.data ?? []}
          pageSize={10}
          getRowId={(env) => env.id}
          emptyState={
            <EmptyState
              icon={<ServerCog />}
              title="No environments"
              description="Create an environment to get a webhook and API key for receiving reports."
              action={
                <Can
                  action={REPORTS_PERMISSIONS.environmentCreate.action}
                  resource={REPORTS_PERMISSIONS.environmentCreate.resource}
                >
                  {addButton}
                </Can>
              }
            />
          }
        />
      )}

      <NewEnvironmentWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        reportProjectId={reportProjectId}
        onProvisioned={(environment, apiKey) =>
          setKeyModal({
            variant: 'create',
            environmentName: environment.name,
            environmentId: environment.id,
            apiKey,
          })
        }
      />

      <ConfirmDialog
        open={rotateTarget !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRotateTarget(null);
          }
        }}
        title="Rotate API key?"
        description="The current key stops working immediately. Senders still using it will fail until they switch to the new key."
        confirmLabel={rotateState.isLoading ? 'Rotating…' : 'Rotate key'}
        cancelLabel="Cancel"
        variant="destructive"
        isLoading={rotateState.isLoading}
        onConfirm={() => void handleRotateConfirm()}
      />

      <ApiKeyOnceModal
        state={keyModal}
        onAcknowledge={() => {
          // P6: dropping the state (plus the mutation-cache reset) is the ONLY
          // exit for the plaintext — nothing here persists it.
          setKeyModal(null);
          rotateState.reset();
        }}
      />
    </section>
  );
}

/** §8.1 meta-line: constraints visible where they apply (D4). */
export function environmentMetaLine(env: EnvironmentView): string {
  const threshold =
    env.duplicateThreshold === 0 ? 'Threshold unlimited' : `Threshold ${env.duplicateThreshold}`;
  const storage = env.maxResources?.storage ?? 0;
  const files =
    storage === 0 ? 'file uploads off' : `${storage} file${storage === 1 ? '' : 's'} per report`;
  const extraServices = Object.keys(env.maxResources ?? {}).filter(
    (service) => service !== 'storage' && (env.maxResources[service] ?? 0) > 0,
  ).length;
  return `${threshold} · ${files}${extraServices > 0 ? ` · +${extraServices} more limits` : ''}`;
}
